import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ban, Check, Plus, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatIsoDay, formatMoney, formatTRY } from "@/lib/format";
import { Field, PROMISE_OPEN, PromiseBadge, Stat } from "@/lib/finance";
import { EmptyRow, LoadingRow, PageHeader } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";
import PromiseDialog from "@/components/finance/PromiseDialog";
import CollectionDialog from "@/components/finance/CollectionDialog";

export const PROMISE_FILTERS = [["", "Tümü"], ["acik", "Açık"], ["bekliyor", "Bekliyor"], ["kismen", "Kısmen Ödendi"], ["gecikti", "Gecikti"], ["odendi", "Ödendi"], ["iptal", "İptal"]];
export const filterParams = (k) => (k === "acik" ? { status: PROMISE_OPEN.join(",") } : { status: k });

function MarkPaidDialog({ p, onClose, onDone }) {
  const [pays, setPays] = useState([]);
  const [pid, setPid] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { api.get("/tahsilatlar", { params: { customer_id: p.customer_id, page_size: 30 } }).then(({ data }) => setPays((data.items || []).filter((x) => x.status === "aktif"))).catch(() => {}); }, [p.customer_id]);
  const save = async () => {
    try { await api.post(`/odeme-sozleri/${p.id}/mark-paid`, { payment_id: pid || null, note: note || null }); toast.success(`${p.promise_no} Ödendi`); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="promise-paid-dialog">
        <DialogHeader><DialogTitle>Ödendi işaretle · {p.promise_no}</DialogTitle></DialogHeader>
        <Field label="Bağlı tahsilat (opsiyonel)">
          <select className="h-9 w-full px-2 rounded-md border border-slate-200 bg-white text-sm" value={pid} onChange={(e) => setPid(e.target.value)} data-testid="promise-paid-payment">
            <option value="">— Tahsilat bağlama —</option>
            {pays.map((x) => <option key={x.id} value={x.id}>{x.payment_no} · {formatIsoDay(x.date)} · {formatTRY(x.amount)}</option>)}
          </select>
        </Field>
        <Field label="Not"><Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} data-testid="promise-paid-note" /></Field>
        <p className="text-[11px] text-slate-500">Muhasebe kaydı oluşmaz; yalnızca sözün durumu değişir.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Vazgeç</Button><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="promise-paid-submit"><Check className="h-4 w-4 mr-1" />Ödendi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PromisesTable({ res, onChanged, onCollect, showCustomer = true, highlight }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canAct = can("tahsilatlar", "create");
  const [paid, setPaid] = useState(null);
  const cols = showCustomer ? 10 : 9;
  const cancel = async (p) => {
    const reason = window.prompt(`${p.promise_no} iptal edilsin mi? Gerekçe (zorunlu):`);
    if (!reason || reason.trim().length < 3) { if (reason !== null) toast.error("Gerekçe en az 3 karakter olmalı"); return; }
    try { await api.post(`/odeme-sozleri/${p.id}/cancel`, { reason: reason.trim() }); toast.success(`${p.promise_no} iptal edildi`); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <table className="erp-table" data-testid="promises-table">
        <thead><tr><th>Söz No</th>{showCustomer && <th>Cari</th>}<th>Satış</th><th>Temsilci</th><th>Söz Tarihi</th><th className="text-right">Tutar</th><th className="text-right">Ödenen</th><th>Not</th><th>Durum</th><th className="text-right">İşlem</th></tr></thead>
        <tbody>
          {!res ? <LoadingRow cols={cols} /> : res.items.length === 0 ? <EmptyRow cols={cols} text="Ödeme sözü yok" /> : res.items.map((p) => (
            <tr key={p.id} className={`${p.status === "gecikti" ? "bg-rose-50/60" : ""} ${highlight === p.id ? "ring-2 ring-inset ring-blue-500" : ""}`} data-testid={`promise-row-${p.promise_no}`}>
              <td className="font-mono text-xs font-semibold text-slate-900">{p.promise_no}</td>
              {showCustomer && <td className="max-w-[220px] truncate">{p.customer_title}</td>}
              <td>{p.order_id ? <button className="font-mono text-[11px] text-blue-700 hover:underline" onClick={() => navigate(`/siparisler/${p.order_id}`)} data-testid={`promise-order-${p.promise_no}`}>{p.invoice_no || p.order_no}</button> : <span className="text-slate-300">—</span>}</td>
              <td className="text-xs">{p.sales_rep_name}</td>
              <td className={`font-mono text-xs ${p.status === "gecikti" ? "text-rose-600 font-semibold" : ""}`}>{formatIsoDay(p.promise_date)}</td>
              <td className="text-right font-mono font-semibold">{formatMoney(p.amount, p.currency)}</td>
              <td className="text-right font-mono text-xs" data-testid={`promise-paid-${p.promise_no}`}>{p.paid_amount ? formatMoney(p.paid_amount, p.currency) : "—"}</td>
              <td className="max-w-[240px] truncate text-slate-500" title={[p.status_note || p.note, p.contact_person && `Görüşülen: ${p.contact_person}`, p.planned_method_label && `Plan: ${p.planned_method_label}`].filter(Boolean).join(" · ")}>{p.status_note || p.note}</td>
              <td><PromiseBadge status={p.status} testid={`promise-status-${p.promise_no}`} /></td>
              <td className="text-right">
                {canAct && p.is_open && (
                  <div className="inline-flex gap-1">
                    {onCollect && <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onCollect(p)} data-testid={`promise-collect-${p.promise_no}`}><Wallet className="h-3.5 w-3.5 mr-1" />Tahsil Et</Button>}
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-emerald-700" title="Ödendi işaretle" onClick={() => setPaid(p)} data-testid={`promise-kept-${p.promise_no}`}><Check className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-rose-700" title="İptal" onClick={() => cancel(p)} data-testid={`promise-cancel-${p.promise_no}`}><Ban className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {paid && <MarkPaidDialog p={paid} onClose={() => setPaid(null)} onDone={() => { setPaid(null); onChanged(); }} />}
    </>
  );
}

export function PromiseFilterChips({ summary, value, onChange, prefix = "promises-filter" }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${prefix}-chips`}>
      {PROMISE_FILTERS.map(([k, l]) => (
        <button key={k || "all"} onClick={() => onChange(k)} data-testid={`${prefix}-${k || "all"}`}
          className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${value === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
          {l}{k && summary?.[k] ? ` · ${summary[k].count}` : ""}
        </button>
      ))}
    </div>
  );
}

export default function Promises() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [filter, setFilter] = useState("acik");
  const [res, setRes] = useState(null);
  const [reload, setReload] = useState(0);
  const [creating, setCreating] = useState(false);
  const [collect, setCollect] = useState(null);
  useEffect(() => {
    setRes(null);
    api.get("/odeme-sozleri", { params: { ...filterParams(filter), page_size: 200 } }).then(({ data }) => setRes(data));
  }, [filter, reload]);
  const s = res?.summary || {};
  return (
    <div data-testid="promises-page">
      <PageHeader title="Ödeme Sözleri" subtitle="Müşterilerden alınan ödeme taahhütleri · tahsilatlarla otomatik eşleşir · muhasebe kaydı oluşturmaz" testid="promises-title">
        {can("tahsilatlar", "create") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="new-promise-button"><Plus className="h-4 w-4 mr-1" />Yeni Ödeme Sözü</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        {PROMISE_FILTERS.slice(1).map(([k, l]) => <Stat key={k} label={l} value={formatTRY(s[k]?.amount || 0)} hint={`${s[k]?.count || 0} söz${["odendi", "iptal"].includes(k) ? "" : " · kalan"}`}
          tone={k === "gecikti" ? "text-rose-600" : "text-slate-900"} active={filter === k} onClick={() => setFilter(k)} testid={`promises-filter-${k}`} />)}
      </div>
      <div className="mb-3"><button className="text-[12px] text-blue-700 hover:underline" onClick={() => setFilter("")} data-testid="promises-filter-all">Tümünü göster ({res?.total ?? "…"} listede)</button></div>
      <div className="panel overflow-x-auto"><PromisesTable res={res} onChanged={() => setReload((r) => r + 1)} onCollect={setCollect} highlight={params.get("id")} /></div>
      {creating && <PromiseDialog onClose={() => setCreating(false)} onDone={() => { setCreating(false); setReload((r) => r + 1); }} />}
      {collect && <CollectionDialog customer={{ id: collect.customer_id, title: collect.customer_title, code: collect.customer_code }} promise={{ ...collect, amount: collect.remaining }}
        onClose={() => setCollect(null)} onDone={() => { setCollect(null); setReload((r) => r + 1); }} />}
    </div>
  );
}
